import { useApolloClient } from "@apollo/client/react/hooks"
import { useTranslation } from "react-i18next"

import { stripTypename } from "@apollo/client/utilities"

import { callMutation } from "shared/api/callMutation"
import { evictProjectPrice } from "shared/api/price/evictPriceCache"
import { invalidateProjectPriceCache } from "shared/api/price/modifyPriceCache"
import { MutationTypename } from "shared/api/types"
import { Logger } from "shared/lib/Logger"
import { getObjectDifference } from "shared/lib/objectUtils"
import { useProjectRequestMonitor } from "shared/lib/request-monitoring/useProjectRequestMonitor"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import { ApolloFetchPolicy } from "shared/types/ApolloFetchPolicy"
import { Project } from "shared/types/Project"
import { UPDATE_PROJECT } from "shared/types/ProjectMutations"

import { useUpdateProjectMutation } from "entities/projects/graphql/mutations.generated"
import { evictProjectHistory } from "entities/projects/graphql/project-history/evictProjectHistory"
import {
  useProjectChildrenLazyQuery,
  useProjectsLazyQuery
} from "entities/projects/graphql/queries.generated"
import { readProjectFragment } from "entities/projects/lib/readProjectFragment"
import z from "zod"

const UpdateProjectInputSchema = z.object({
  id: z.uuid(),
  title: z.string().nullable().optional(),
  description: z.string().nullable().optional(),

  priceList: z.string().nullable().optional(),
  inflationSurcharge: z.number().nullable().optional(),

  phase: z.any().nullable().optional(),

  ui: z.any().nullable().optional()
})

type UpdateProjectInput = z.infer<typeof UpdateProjectInputSchema>

function getUpdateProjectInput(project: Partial<Project> & { id: string }) {
  try {
    return z.parse(UpdateProjectInputSchema, project)
  } catch (e) {
    Logger.error("InvalidArgumentError", "invalid input for project update", {
      project
    })
    throw e
  }
}

function didPriceRelevantAttributesChange(
  input: UpdateProjectInput | undefined
) {
  return (
    input?.inflationSurcharge !== undefined || input?.priceList !== undefined
  )
}

export function useUpdateProject() {
  const { t } = useTranslation()
  const { cache } = useApolloClient()
  const [mutate, rest] = useUpdateProjectMutation()
  const startProjectRequest = useProjectRequestMonitor()
  const [fetchProjects] = useProjectsLazyQuery()
  const [fetchProject] = useProjectChildrenLazyQuery({
    fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
  })
  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      loading: t("Request.Project.UPDATE_PROJECT.Start"),
      success: t("Request.Project.UPDATE_PROJECT.Success"),
      failure: t("Request.Project.UPDATE_PROJECT.Failure")
    }
  })

  const updateProject = useEvent((id: string, project: Partial<Project>) => {
    const cachedData = readProjectFragment(id, cache)
    const changedValues = getObjectDifference(project, cachedData || {})
    if (Object.keys(changedValues).length === 0) return undefined
    const input: UpdateProjectInput = stripTypename(
      getUpdateProjectInput({ id, ...changedValues })
    )

    if (didPriceRelevantAttributesChange(input)) {
      invalidateProjectPriceCache(id, cache, true)
    }

    const { raiseSuccessToast, raiseErrorToast } = raiseLoadingToast()
    const completeProjectRequest = startProjectRequest({
      projectID: id,
      requestType: UPDATE_PROJECT
    })

    return callMutation(mutate, {
      variables: { id, input },
      transform: data => data,
      apolloOptions: {
        optimisticResponse: cachedData
          ? {
              __typename: MutationTypename,
              project: { ...cachedData, ...project }
            }
          : undefined,
        onCompleted: () => {
          if (didPriceRelevantAttributesChange(input)) {
            evictProjectPrice(id, cache, true)
          }
          completeProjectRequest()
          fetchProject({ variables: { id } })
          evictProjectHistory(id, cache)
          raiseSuccessToast()
        },
        onError: () => {
          completeProjectRequest()
          fetchProject({ variables: { id } })
          evictProjectHistory(id, cache)
          evictProjectPrice(id, cache, true)
          fetchProjects()
          raiseErrorToast()
        }
      }
    })
  })

  return {
    updateProject,
    ...rest
  }
}
